import { motion, useInView } from "motion/react";
import { useRef } from "react";
import { TricolorStripe } from "./folk-pattern";

const drivers = [
  {
    name: "David Toth",
    displayName: "Dávid Tóth",
    photo: "/drivers/davesz-432x540.png",
    achievement: "INDYCAR Pro Series · F4 CEZ Academy",
    iRating: "10,069 iR",
  },
  {
    name: "Tamas Simon",
    displayName: "Tamás Simon",
    photo: "/drivers/simont-432x540.png",
    achievement: "iRacing Pro License · Indy 500 Competitor",
    iRating: "9,260 iR",
  },
  {
    name: "Norbert Kiss",
    displayName: "Norbert Kiss",
    photo: "/drivers/norbi-432x540.png",
    achievement: "7x European Truck Racing Champion",
    iRating: "10,037 iR",
  },
  {
    name: "Balazs Palla",
    displayName: "Balázs Palla",
    photo: "/drivers/balu-432x540.png",
    achievement: "Foxxtecca Championship Leader · DNLS Winner",
    iRating: "10,009 iR",
  },
  {
    name: "Daniel Olah",
    displayName: "Dániel Oláh",
    photo: "/drivers/olahd-432x540.png",
    achievement: "Hungarian National E-Sports Champion",
    iRating: "9,684 iR",
  },
  {
    name: "Mate Varga",
    displayName: "Máté Varga",
    photo: "/drivers/vargam-432x540.png",
    achievement: "Chicago Sim Gaming Expo Bronze",
    iRating: "8,076 iR",
  },
];

export function DriversSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="relative py-24 px-4" ref={ref}>
      {/* Subtle Kalocsai corner — bottom-right */}
      <img
        src="/kalocsai-corner-br.webp"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute bottom-0 right-0 w-60 h-60 object-contain"
        style={{
          opacity: 0.06,
          maskImage: "radial-gradient(ellipse at bottom right, black 30%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at bottom right, black 30%, transparent 70%)",
        }}
      />
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-foreground md:text-5xl">
            Built by <span className="text-primary">Champions</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Our setups are crafted by drivers who compete at the highest level of sim racing and
            real-world motorsport.
          </p>
        </motion.div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {drivers.map((driver, i) => (
            <motion.div
              key={driver.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
              className="group relative flex items-center gap-4 rounded-xl border border-border bg-card/50 p-5 backdrop-blur-sm transition-all duration-300 hover:border-folk-blue/30 hover:bg-card overflow-hidden"
            >
              {/* Driver photo */}
              <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full border-2 border-border group-hover:border-folk-blue/50 transition-colors">
                <img
                  src={driver.photo}
                  alt={driver.displayName}
                  loading="lazy"
                  width={80}
                  height={80}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-foreground">{driver.displayName}</h3>
                <p className="text-sm text-muted-foreground truncate">{driver.achievement}</p>
                <span
                  className="mt-1 inline-block text-xs font-semibold text-folk-blue"
                  style={{
                    fontFamily: "'JetBrains Mono Variable', monospace",
                  }}
                >
                  {driver.iRating === "Legend" ? "Racing Legend" : `${driver.iRating} iRating`}
                </span>
              </div>

              {/* Hungarian tricolor accent stripe on left */}
              <div className="absolute left-0 top-1/4 h-1/2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <TricolorStripe orientation="vertical" className="h-full" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
